import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import TextInput from '../../../../components/UI/TextInput';
import { Heading, Text } from '../../../../components/Typography';
import DocumentSections from './DocumentSections';
import styles from './SectionSelector.module.scss';
import { RootState } from '../../../../store';
import { setMaxCharsPerSection } from '../../../../store/summaryTest/slice';

const SectionSelector: React.FC = () => {
  const dispatch = useDispatch();
  const { selectedDocuments, documents, isLoadingSections, maxCharsPerSection } = useSelector(
    (state: RootState) => state.summaryTest,
  );

  const handleMaxCharsChange = (value: string) => {
    const numValue = value === '' ? null : parseInt(value, 10);
    if (numValue === null || (numValue > 0 && !isNaN(numValue))) {
      dispatch(setMaxCharsPerSection(numValue));
    }
  };

  const selectedDocIds = Object.keys(selectedDocuments);
  if (selectedDocIds.length === 0) {
    return (
      <div className={styles.container}>
        <Heading level={3} className={styles.header}>
          Document Sections
        </Heading>
        <Text color="neutral-500" className={styles.noDocuments}>
          Select documents first to view their sections
        </Text>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Heading level={3}>Document Sections</Heading>
      </div>

      {/* Max chars per section input */}
      <div className={styles.maxCharsInput}>
        <TextInput
          label="Max characters per section (optional)"
          value={maxCharsPerSection?.toString() || ''}
          onChange={handleMaxCharsChange}
          placeholder="e.g., 5000"
          type="number"
          min="1"
        />
        {maxCharsPerSection && (
          <Text size="small" color="neutral-600" className={styles.maxCharsHelp}>
            Sections longer than {maxCharsPerSection.toLocaleString()} characters will be truncated
          </Text>
        )}
      </div>

      {isLoadingSections && (
        <Text color="neutral-500" className={styles.loadingContainer}>
          Loading sections...
        </Text>
      )}

      {!isLoadingSections && documents.length === 0 && (
        <Text color="neutral-500" className={styles.emptyState}>
          No sections found for selected documents.
        </Text>
      )}

      {!isLoadingSections &&
        documents
          .filter((doc) => selectedDocIds.includes(doc.id))
          .map((doc) => (
            <DocumentSections
              key={doc.id}
              documentId={doc.id}
              documentName={doc.name}
              sections={doc.sections}
              maxCharsPerSection={maxCharsPerSection}
            />
          ))}
    </div>
  );
};

export default SectionSelector;
